import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Link, useNavigate } from 'react-router-dom';
import { Dashboard } from '../components/Dashboard';
import { motion, AnimatePresence } from 'framer-motion';
import {
    UploadCloud,
    Calendar as CalendarIcon,
    Users,
    ChevronRight,
    FileSpreadsheet,
    LayoutGrid,
    Search,
    GraduationCap,
    CheckCircle2,
    Clock,
    ChevronDown,
    Loader2
} from 'lucide-react';

export const HomePage = () => {
    const { students, handleFileUpload, getDashboardStats, days, isAuthenticated, getUploadStatistics, userRole } = useApp();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [studentSearchQuery, setStudentSearchQuery] = useState('');
    const [uploadStats, setUploadStats] = useState([]);
    const [loadingStats, setLoadingStats] = useState(false);
    const [expandedDate, setExpandedDate] = useState(null);

    useEffect(() => {
        if (isAuthenticated) {
            fetchStats();
        }
    }, [isAuthenticated]);

    const fetchStats = async () => {
        setLoadingStats(true);
        try {
            const data = await getUploadStatistics();
            setUploadStats(data);
        } catch (error) {
            console.error("Failed to fetch statistics", error);
        } finally {
            setLoadingStats(false);
        }
    };

    const hasData = students.length > 0;
    const stats = getDashboardStats();

    // Получаем все группы из всех дней с информацией о дне
    const getAllGroups = () => {
        const allGroups = [];
        days.forEach((day) => {
            day.groups.forEach((group) => {
                allGroups.push({
                    ...group,
                    dayInfo: {
                        id: day.id,
                        date: day.date
                    }
                });
            });
        });
        return allGroups;
    };

    const allGroups = getAllGroups();
    const filteredGroups = searchQuery
        ? allGroups.filter(group =>
            group.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : [];

    // Фильтрация студентов по поисковому запросу
    const filteredStudents = studentSearchQuery
        ? students.filter(student =>
            student.name.toLowerCase().includes(studentSearchQuery.toLowerCase())
        )
        : [];

    return (
        <div className="min-h-screen w-full bg-[#0f172a] text-slate-200 pb-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">

                {!hasData ? (
                    // --- КРАСИВЫЙ EMPTY STATE ---
                    <div className="flex flex-col items-center justify-center min-h-[70vh]">
                        <div className="w-full max-w-2xl p-1 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl shadow-2xl">
                            <div className="bg-slate-900 rounded-[22px] p-8 md:p-12 text-center">
                                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-indigo-500/10 text-indigo-400 mb-8 ring-1 ring-indigo-500/20">
                                    <UploadCloud size={40} />
                                </div>

                                <h2 className="text-4xl font-extrabold text-white mb-4 tracking-tight">
                                    {userRole === 'read_only' ? 'Нет данных' : 'Начните работу'}
                                </h2>
                                <p className="text-slate-400 text-lg mb-10 max-w-md mx-auto">
                                    {userRole === 'read_only'
                                        ? 'В данный момент база студентов пуста. Обратитесь к администратору.'
                                        : 'Загрузите список студентов в формате Excel, чтобы система сформировала график посещаемости.'}
                                </p>

                                {userRole !== 'read_only' && (
                                    <label className="relative group cursor-pointer inline-block w-full">
                                        <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl blur opacity-25 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                                        <div className="relative flex items-center justify-center gap-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-5 px-8 rounded-xl transition-all shadow-xl">
                                            <FileSpreadsheet size={24} />
                                            <span>Выбрать файл Excel</span>
                                            <input type="file" onChange={handleFileUpload} accept=".xlsx, .xls" className="hidden" />
                                        </div>
                                    </label>
                                )}

                                {userRole !== 'read_only' && (
                                    <div className="mt-12 grid grid-cols-3 gap-4 border-t border-white/5 pt-8">
                                        <div className="text-left px-4">
                                            <p className="text-indigo-400 font-semibold mb-1">Шаг 1</p>
                                            <p className="text-xs text-slate-500 leading-relaxed">Подготовьте файл .xlsx с колонками ФИО</p>
                                        </div>
                                        <div className="text-left px-4 border-x border-white/5">
                                            <p className="text-purple-400 font-semibold mb-1">Шаг 2</p>
                                            <p className="text-xs text-slate-500 leading-relaxed">Загрузите его в систему одним кликом</p>
                                        </div>
                                        <div className="text-left px-4">
                                            <p className="text-pink-400 font-semibold mb-1">Шаг 3</p>
                                            <p className="text-xs text-slate-500 leading-relaxed">Управляйте группами и отметками</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    // --- КРАСИВЫЙ DASHBOARD ---
                    <div className="space-y-10 animate-in fade-in duration-700">
                        <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                            <div>
                                <h1 className="text-4xl font-black text-white tracking-tight">Рабочая панель</h1>
                                <p className="text-slate-400 mt-1">Общая сводка по посещаемости и активным дням</p>
                            </div>
                            <div className="px-4 py-2 bg-slate-800/50 border border-white/10 rounded-lg backdrop-blur-sm text-sm font-medium text-slate-300">
                                Студентов в базе: <span className="text-indigo-400">{students.length}</span>
                            </div>
                        </header>

                        <Dashboard stats={stats} />

                        {/* История подтверждений */}
                        {isAuthenticated && (
                            <div className="mt-10">
                                <div className="mb-6">
                                    <h2 className="text-2xl font-bold text-white mb-2">История подтверждений</h2>
                                    <p className="text-slate-400 text-sm">Статистика по датам подтверждения студентов</p>
                                </div>

                                {loadingStats ? (
                                    <div className="flex items-center justify-center py-16">
                                        <Loader2 className="animate-spin text-indigo-500" size={32} />
                                    </div>
                                ) : uploadStats.length === 0 ? (
                                    <div className="bg-slate-800/30 border border-white/5 rounded-2xl p-12 text-center">
                                        <CalendarIcon size={48} className="mx-auto text-slate-600 mb-4" />
                                        <p className="text-slate-400">Пока нет подтвержденных студентов</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {uploadStats.map((stat, index) => (
                                            <div key={index} className="bg-slate-800/30 border border-white/5 rounded-xl overflow-hidden">
                                                <button
                                                    onClick={() => setExpandedDate(expandedDate === stat.date ? null : stat.date)}
                                                    className="w-full p-4 flex items-center justify-between hover:bg-slate-800/50 transition-colors"
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className="bg-indigo-500/10 p-3 rounded-lg">
                                                            <CalendarIcon className="text-indigo-400" size={20} />
                                                        </div>
                                                        <div className="text-left">
                                                            <p className="font-semibold text-white">
                                                                {new Date(stat.date).toLocaleDateString('ru-RU', {
                                                                    day: 'numeric',
                                                                    month: 'long',
                                                                    year: 'numeric'
                                                                })}
                                                            </p>
                                                            <p className="text-sm text-slate-400">
                                                                {stat.count} студ. • {stat.groups.length} групп
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <ChevronDown
                                                        className={`text-slate-400 transition-transform ${expandedDate === stat.date ? 'rotate-180' : ''}`}
                                                        size={20}
                                                    />
                                                </button>

                                                <AnimatePresence>
                                                    {expandedDate === stat.date && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.2 }}
                                                            className="border-t border-white/5"
                                                        >
                                                            <div className="p-4 space-y-4">
                                                                {/* Groups */}
                                                                <div>
                                                                    <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Группы</p>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {stat.groups.map((group, gIndex) => (
                                                                            <span key={gIndex} className="px-3 py-1 bg-slate-700/50 rounded-full text-sm text-slate-300">
                                                                                {group}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>

                                                                {/* Students */}
                                                                <div>
                                                                    <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Студенты</p>
                                                                    <div className="space-y-2 max-h-64 overflow-y-auto">
                                                                        {stat.students.map((student, sIndex) => (
                                                                            <div key={sIndex} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                                                                                <div className="flex items-center gap-3">
                                                                                    <div className="bg-indigo-500/10 p-2 rounded-lg">
                                                                                        <GraduationCap className="text-indigo-400" size={16} />
                                                                                    </div>
                                                                                    <div>
                                                                                        <p className="text-sm font-medium text-white">{student.name}</p>
                                                                                        <p className="text-xs text-slate-500">{student.group}</p>
                                                                                    </div>
                                                                                </div>
                                                                                <span className="text-xs px-2 py-1 bg-green-500/10 text-green-400 rounded">
                                                                                    Подтвержден
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Глобальный поиск групп */}
                        <div className="mb-10">
                            <div className="flex items-center gap-2 mb-6">
                                <Search className="text-indigo-400" size={24} />
                                <h2 className="text-2xl font-bold text-white">Поиск групп</h2>
                            </div>

                            <div className="relative mb-6">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                                <input
                                    type="text"
                                    placeholder="Найти группу по названию..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full bg-slate-900/50 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all"
                                />
                            </div>

                            {/* Результаты поиска */}
                            {searchQuery && (
                                <div className="mb-8">
                                    {filteredGroups.length === 0 ? (
                                        <div className="bg-slate-900/30 backdrop-blur-md border border-white/5 rounded-[24px] p-12 text-center text-slate-500">
                                            <Search size={48} className="mx-auto mb-4 opacity-20" />
                                            <p className="text-lg">Группы не найдены</p>
                                            <p className="text-sm">Попробуйте изменить запрос</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <p className="text-sm text-slate-500 mb-4">
                                                Найдено групп: <span className="text-indigo-400 font-bold">{filteredGroups.length}</span>
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {filteredGroups.map((group) => (
                                                    <div
                                                        key={`${group.dayInfo.id}-${group.id}`}
                                                        onClick={() => navigate(`/group/${group.id}`)}
                                                        className="group cursor-pointer bg-slate-900/40 backdrop-blur-md border border-white/5 rounded-2xl p-5 hover:bg-slate-800/60 transition-all"
                                                    >
                                                        <div className="flex items-start justify-between mb-4">
                                                            <div className="p-2.5 bg-indigo-500/10 rounded-xl text-indigo-400 group-hover:bg-indigo-500 group-hover:text-white transition-colors">
                                                                <GraduationCap size={20} />
                                                            </div>
                                                            <ChevronRight className="text-slate-600 group-hover:text-white group-hover:translate-x-1 transition-all" size={18} />
                                                        </div>

                                                        <h3 className="text-lg font-bold text-white mb-2 group-hover:text-indigo-400 transition-colors">
                                                            {group.name}
                                                        </h3>

                                                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                                                            <CalendarIcon size={12} />
                                                            <span>
                                                                {group.dayInfo.date.toLocaleDateString('ru-RU', {
                                                                    day: 'numeric',
                                                                    month: 'long'
                                                                })}
                                                            </span>
                                                        </div>

                                                        <div className="flex items-center gap-2 text-sm text-slate-400">
                                                            <Users size={14} />
                                                            <span>{group.students.length} студентов</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Глобальный поиск студентов */}
                        <div className="mb-10">
                            <div className="flex items-center gap-2 mb-6">
                                <Search className="text-emerald-400" size={24} />
                                <h2 className="text-2xl font-bold text-white">Поиск студентов</h2>
                            </div>

                            <div className="relative mb-6">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                                <input
                                    type="text"
                                    placeholder="Найти студента по имени или фамилии..."
                                    value={studentSearchQuery}
                                    onChange={(e) => setStudentSearchQuery(e.target.value)}
                                    className="w-full bg-slate-900/50 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
                                />
                            </div>

                            {/* Результаты поиска студентов */}
                            {studentSearchQuery && (
                                <div className="mb-8">
                                    {filteredStudents.length === 0 ? (
                                        <div className="bg-slate-900/30 backdrop-blur-md border border-white/5 rounded-[24px] p-12 text-center text-slate-500">
                                            <Search size={48} className="mx-auto mb-4 opacity-20" />
                                            <p className="text-lg">Студенты не найдены</p>
                                            <p className="text-sm">Попробуйте изменить запрос</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <p className="text-sm text-slate-500 mb-4">
                                                Найдено студентов: <span className="text-emerald-400 font-bold">{filteredStudents.length}</span>
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {filteredStudents.map((student) => {
                                                    // Найдем группу студента
                                                    let studentGroup = null;
                                                    allGroups.forEach(group => {
                                                        if (group.students.some(s => s.id === student.id)) {
                                                            studentGroup = group;
                                                        }
                                                    });

                                                    return (
                                                        <div
                                                            key={student.id}
                                                            onClick={() => studentGroup && navigate(`/group/${studentGroup.id}`)}
                                                            className={`group bg-slate-900/40 backdrop-blur-md border border-white/5 rounded-2xl p-5 transition-all ${studentGroup ? 'cursor-pointer hover:bg-slate-800/60' : ''}`}
                                                        >
                                                            <div className="flex items-start justify-between mb-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-lg font-bold text-slate-300 border border-white/10 group-hover:border-emerald-500/50 transition-colors">
                                                                        {student.name.charAt(0)}
                                                                    </div>
                                                                    <div>
                                                                        <h3 className="text-base font-bold text-white group-hover:text-emerald-400 transition-colors">
                                                                            {student.name}
                                                                        </h3>
                                                                        {student.confirmed ? (
                                                                            <div className="inline-flex items-center gap-1 text-xs text-emerald-400 mt-1">
                                                                                <CheckCircle2 size={12} /> Подтвержден
                                                                            </div>
                                                                        ) : (
                                                                            <div className="inline-flex items-center gap-1 text-xs text-amber-400 mt-1">
                                                                                <Clock size={12} /> Ожидает
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {studentGroup && (
                                                                <div className="space-y-2 pt-3 border-t border-white/5">
                                                                    <div className="flex items-center gap-2 text-xs text-slate-400">
                                                                        <GraduationCap size={12} />
                                                                        <span className="font-medium">{studentGroup.name}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                                                        <CalendarIcon size={12} />
                                                                        <span>
                                                                            {studentGroup.dayInfo.date.toLocaleDateString('ru-RU', {
                                                                                day: 'numeric',
                                                                                month: 'long'
                                                                            })}
                                                                        </span>
                                                                    </div>
                                                                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full text-indigo-400 text-[10px] font-bold uppercase">
                                                                        {student.studyType}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <div className="flex items-center gap-2 mb-6">
                                <CalendarIcon className="text-indigo-400" size={24} />
                                <h2 className="text-2xl font-bold text-white">Календарь занятий</h2>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {days.map((day) => (
                                    <Link key={day.id} to={`/day/${day.id}`} className="group relative">
                                        {/* Эффект свечения при ховере */}
                                        <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-20 transition duration-300"></div>

                                        <div className="relative h-full bg-slate-800/40 backdrop-blur-md border border-white/10 rounded-2xl p-6 transition-all duration-300 group-hover:-translate-y-1 group-hover:bg-slate-800/60">
                                            <div className="flex justify-between items-start mb-6">
                                                <div className="p-3 bg-indigo-500/10 rounded-xl text-indigo-400 group-hover:bg-indigo-500 group-hover:text-white transition-colors">
                                                    <CalendarIcon size={24} />
                                                </div>
                                                <ChevronRight className="text-slate-600 group-hover:text-white group-hover:translate-x-1 transition-all" size={20} />
                                            </div>

                                            <h3 className="text-xl font-bold text-white mb-1 capitalize">
                                                {day.date.toLocaleDateString('ru-RU', { weekday: 'long' })}
                                            </h3>
                                            <p className="text-slate-400 text-sm mb-6">
                                                {day.date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}
                                            </p>

                                            <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/5">
                                                <div className="flex items-center gap-2">
                                                    <LayoutGrid size={16} className="text-slate-500" />
                                                    <span className="text-sm font-medium">{day.groups.length} групп</span>
                                                </div>
                                                <div className="flex items-center gap-2 justify-end">
                                                    <Users size={16} className="text-slate-500" />
                                                    <span className="text-sm font-medium">{day.totalStudents} чел.</span>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};